/**
 * Hosted-page error parsing. Local on purpose — the billing workspace's
 * parseBillingError is tuned to admin concerns (three-gate lockouts) and pay
 * must not import the admin-oriented billing tree.
 */
export const unwrap = (res) => res?.data?.data ?? res?.data;

/**
 * @returns {{type: string, code: string|null, message: string, retryAfter: number|null}}
 */
export const parseHostedError = (err) => {
	if (err?.stripeMessage) {
		return { type: 'stripe', code: 'stripe_error', message: err.stripeMessage, retryAfter: null };
	}

	const response = err?.response;
	if (!response) {
		return {
			type: 'network',
			code: null,
			message: 'We couldn’t reach the server. Check your connection and try again.',
			retryAfter: null,
		};
	}

	const code = response.data?.error ?? null;
	const retryAfter = Number(response.headers?.['retry-after']) || null;

	switch (response.status) {
		case 401:
			return { type: 'session', code: 'session_expired', message: 'Your session expired.', retryAfter };
		case 404:
			return { type: 'dead_link', code: code ?? 'link_not_found', message: 'This link isn’t valid.', retryAfter };
		case 410:
			return {
				type: 'dead_link',
				code: code ?? 'link_expired',
				message: code === 'link_no_longer_valid' ? 'This link is no longer valid.' : 'This link has expired.',
				retryAfter,
			};
		case 422:
			return {
				type: 'validation',
				code: code ?? 'validation',
				message:
					code === 'verification_failed'
						? 'That doesn’t match our records. Check the spelling and try again.'
						: 'Please check the highlighted fields.',
				retryAfter,
			};
		case 409:
			return {
				type: 'conflict',
				code,
				message:
					{
						payment_method_required: 'Please add a payment method first.',
						autopay_required_by_school: 'Your school requires Auto-Pay for tuition plans.',
						assignment_not_mutable: 'This tuition plan is no longer active.',
					}[code] ?? 'That change isn’t possible right now.',
				retryAfter,
			};
		case 429:
			return {
				type: 'throttled',
				code: 'throttled',
				message: retryAfter
					? `Too many attempts — try again in ${retryAfter} seconds.`
					: 'Too many attempts — wait a minute and try again.',
				retryAfter,
			};
		case 502:
			return {
				type: 'stripe',
				code: 'stripe_error',
				message: 'Our payment provider had a problem. Please try again.',
				retryAfter,
			};
		default:
			return { type: 'unknown', code, message: 'Something went wrong. Please try again.', retryAfter };
	}
};
